/**
 * ToolGhostPreview — the CAD-side rubber-band ghost layer (Faz 11.8).
 *
 * The only subscriber of the high-frequency `toolPreview` slice inside the
 * 2D/3D SceneContent (the BimViewport keeps its own layer — same isolation
 * pattern): pointer-move preview writes reconcile just this subtree.
 *
 * Renders the classic command ghosts the modify tools publish:
 *  - moveDrag (MOVE drag / COPY displacement): dashed base→cursor line plus
 *    translucent wireframe ghosts of the selected entities translated by the
 *    live delta,
 *  - ROTATE: base→reference and center→cursor rays with the swept arc; the
 *    live angle rides the dynamic-input balloon readout (B3 source).
 *
 * DRAGMODE (sysvar 0/1/2) gates every ghost: 0 = Off hides them all — the
 * classic "no outline during drag" parity.
 */

import { useMemo } from 'react';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useAppStore, type Entity } from '../state/store';
import { useSystemVarsStore } from '../state/useSystemVarsStore';
import { PREVIEW_COLORS } from '../themes/colors';

/** Translucent wireframe ghost of one entity translated by `delta`. */
function GhostEntity({ entity, delta }: { entity: Entity; delta: THREE.Vector3 }) {
    const color = PREVIEW_COLORS.move;
    const d = entity.data as Record<string, any>;
    const geometry = useMemo(() => {
        switch (entity.type) {
            case 'line': {
                if (!Array.isArray(d.start) || !Array.isArray(d.end)) return null;
                return [
                    new THREE.Vector3(d.start[0] + delta.x, d.start[1] + delta.y, (d.start[2] ?? 0) + delta.z),
                    new THREE.Vector3(d.end[0] + delta.x, d.end[1] + delta.y, (d.end[2] ?? 0) + delta.z),
                ];
            }
            case 'circle': {
                if (!Array.isArray(d.center) || typeof d.radius !== 'number') return null;
                const pts: THREE.Vector3[] = [];
                for (let i = 0; i <= 32; i++) {
                    const a = (i / 32) * Math.PI * 2;
                    pts.push(new THREE.Vector3(
                        d.center[0] + delta.x + Math.cos(a) * d.radius,
                        d.center[1] + delta.y,
                        (d.center[2] ?? 0) + delta.z + Math.sin(a) * d.radius
                    ));
                }
                return pts;
            }
            case 'rectangle': {
                if (!Array.isArray(d.corner1) || !Array.isArray(d.corner2)) return null;
                const [x1, y1] = d.corner1;
                const [x2, y2] = d.corner2;
                const z = (d.corner1[2] ?? 0) + delta.z;
                return [
                    new THREE.Vector3(x1 + delta.x, y1 + delta.y, z),
                    new THREE.Vector3(x2 + delta.x, y1 + delta.y, z),
                    new THREE.Vector3(x2 + delta.x, y2 + delta.y, z),
                    new THREE.Vector3(x1 + delta.x, y2 + delta.y, z),
                    new THREE.Vector3(x1 + delta.x, y1 + delta.y, z),
                ];
            }
            case 'polyline':
            case 'polygon': {
                const vs: number[][] = Array.isArray(d.vertices) ? d.vertices : [];
                if (vs.length < 2) return null;
                const pts = vs.map((v) => new THREE.Vector3(v[0] + delta.x, v[1] + delta.y, (v[2] ?? 0) + delta.z));
                if (entity.type === 'polygon') pts.push(pts[0].clone());
                return pts;
            }
            default:
                return null;
        }
    }, [entity, delta]);

    if (!geometry) return null;
    return <Line points={geometry} color={color} lineWidth={1} transparent opacity={0.5} />;
}

export function ToolGhostPreview() {
    const toolPreview = useAppStore((s) => s.toolPreview);
    const dragMode = useSystemVarsStore((s) => s.DRAGMODE);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);

    // DRAGMODE 0 (Off): no outline during drags — ghosts gate here.
    if (!dragMode || !toolPreview?.active) return null;

    const color = PREVIEW_COLORS.move;

    // MOVE/COPY ghost: the displacement line plus translucent translated
    // copies of the selection (the classic rubber-band ghost).
    const moveBase = toolPreview.moveDrag
        ? toolPreview.basePoint
        : toolPreview.toolId === 'COPY' ? toolPreview.start : null;
    const moveCurrent = toolPreview.moveDrag
        ? toolPreview.current
        : toolPreview.toolId === 'COPY' ? toolPreview.current : null;

    if (moveBase && moveCurrent) {
        const delta = new THREE.Vector3().subVectors(moveCurrent, moveBase);
        return (
            <group renderOrder={4}>
                <Line
                    points={[moveBase, moveCurrent]}
                    color={color}
                    lineWidth={1}
                    dashed
                    dashSize={0.2}
                    gapSize={0.1}
                />
                {selectedIds.map((id) => {
                    const e = entities[id];
                    if (!e) return null;
                    return <GhostEntity key={`ghost-${id}`} entity={e} delta={delta} />;
                })}
            </group>
        );
    }

    // ROTATE/SCALE ghost: center→reference and center→cursor rays plus the
    // swept arc (rotate) or the reference pair (scale); the live angle /
    // factor value rides the dynamic-input balloon readout.
    if ((toolPreview.toolId === 'ROTATE' || toolPreview.toolId === 'SCALE')
        && toolPreview.center && toolPreview.currentAngle) {
        const center = toolPreview.center as THREE.Vector3;
        const start = (toolPreview.startAngle ?? toolPreview.currentAngle) as THREE.Vector3;
        const current = toolPreview.currentAngle as THREE.Vector3;
        const r1 = Math.hypot(start.x - center.x, start.y - center.y);
        const r2 = Math.hypot(current.x - center.x, current.y - center.y);
        const radius = Math.max(r1, r2, 1e-3);
        const a0 = Math.atan2(start.y - center.y, start.x - center.x);
        const a1 = Math.atan2(current.y - center.y, current.x - center.x);
        let sweep = a1 - a0;
        while (sweep > Math.PI) sweep -= Math.PI * 2;
        while (sweep < -Math.PI) sweep += Math.PI * 2;
        const arc: THREE.Vector3[] = [];
        const steps = 24;
        for (let i = 0; i <= steps; i++) {
            const a = a0 + (sweep * i) / steps;
            arc.push(new THREE.Vector3(
                center.x + Math.cos(a) * radius,
                center.y + Math.sin(a) * radius,
                center.z
            ));
        }
        return (
            <group renderOrder={4}>
                <Line points={[center, start]} color={color} lineWidth={1} />
                <Line points={[center, current]} color={color} lineWidth={1} />
                <Line points={arc} color={color} lineWidth={1.5} />
            </group>
        );
    }

    return null;
}

export default ToolGhostPreview;
